import React, { useState } from 'react';

import { type FTemplateSectionFragment, type FWaypointTemplateFragment } from '@dashboard/generated/wp/platform';
import { useIsMobile } from '@dashboard/hooks/media-queries';
import { useDndContext } from '@dnd-kit/core';
import { type SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicatorOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/system';

import DraggableTemplateCard from './DraggableTemplateCard';
import { useCurrentTemplateSections, useIsLayoutTab } from './hooks';
import { DeleteSectionButton, RenameSectionButton } from './SectionButtons';
import { useSectionState } from './SectionsStore';

const dropFlash = keyframes({
  '0%': { boxShadow: 'inset 0 0 0 1px rgba(31, 132, 102, 0.7)' },
  '100%': { boxShadow: 'inset 0 0 0 1px rgba(31, 132, 102, 0)' },
});

type TemplateSectionProps = {
  section: FTemplateSectionFragment | null;
  templates: FWaypointTemplateFragment[];
  isRecentDrop: boolean;
};

export default function TemplateSection({ section, templates, isRecentDrop = false }: TemplateSectionProps) {
  const isLayoutTab = useIsLayoutTab();
  const isMobile = useIsMobile();
  const { data: sectionsData } = useCurrentTemplateSections();
  const sections = sectionsData?.TemplateSections ?? [];
  const reorderable = !isMobile && !isLayoutTab && sections.length > 1;

  const isSection = section !== null;
  const {
    attributes: dragAttributes,
    listeners: dragListeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: isSection ? section.id : 'GLOBAL_TEMPLATES',
    data: isSection ? { type: 'section', id: section.id } : { type: 'global', id: 'GLOBAL_TEMPLATES' },
    disabled: { draggable: !isSection || !reorderable, droppable: false },
  });
  const { active } = useDndContext();
  const isAnyDragActive = active != null;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const renderBody = () => {
    if (isSection) {
      return (
        <SectionTemplatesSection
          section={section}
          templates={templates}
          dragListeners={dragListeners}
          isDragging={isDragging}
        />
      );
    }
    return <GlobalTemplatesSection templates={templates} />;
  };

  if (isSection && isLayoutTab) {
    return null;
  }
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...dragAttributes}
      sx={{
        backgroundColor: isOver && !isDragging ? 'action.hover' : isSection ? 'cadet.100' : 'transparent',
        transition: 'background-color 150ms ease',
        ...(isSection && {
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }),
        ...(isRecentDrop && { animation: `${dropFlash} 800ms ease-out` }),
        ...(isAnyDragActive
          ? {}
          : { '&:hover .section-actions, &:has(:focus-visible) .section-actions': { opacity: 1 } }),
      }}
    >
      {renderBody()}
    </Box>
  );
}

function GlobalTemplatesSection({ templates }: { templates: FWaypointTemplateFragment[] }) {
  const isMobile = useIsMobile();
  const isLayoutTab = useIsLayoutTab();

  const { data } = useCurrentTemplateSections();
  const sections = data?.TemplateSections ?? [];

  const templatesDraggable = !isMobile && !isLayoutTab && sections.length > 0;

  const renderBody = () => {
    if (templates.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ gridColumn: 'span 12', textAlign: 'center', py: 2, fontStyle: 'italic' }}
        >
          Drag and drop templates here to ungroup
        </Typography>
      );
    }
    return templates.map((t) => <DraggableTemplateCard key={t.id} template={t} disabled={!templatesDraggable} />);
  };

  return <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>{renderBody()}</Box>;
}

function SectionTemplatesSection({
  section,
  isDragging,
  templates,
  dragListeners,
}: {
  section: FTemplateSectionFragment;
  isDragging: boolean;
  templates: FWaypointTemplateFragment[];
  dragListeners: SyntheticListenerMap | undefined;
}) {
  const isMobile = useIsMobile();

  const { expanded, setExpanded } = useSectionState(section.id);

  const { data } = useCurrentTemplateSections();
  const sections = data?.TemplateSections ?? [];
  const reorderable = !isMobile && sections.length > 1;
  const templatesDraggable = !isMobile && sections.length > 0;
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const renderBody = () => {
    if (templates.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ gridColumn: 'span 12', textAlign: 'center', py: 2, fontStyle: 'italic' }}
        >
          Drag and drop templates here
        </Typography>
      );
    }
    return templates.map((t) => <DraggableTemplateCard key={t.id} template={t} disabled={!templatesDraggable} />);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
      disableGutters
      elevation={0}
      square
      sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlined fontSize="small" />}
        {...(reorderable ? dragListeners : {})}
        sx={{
          px: 2,
          py: 1,
          minHeight: 0,
          flexDirection: 'row-reverse',
          gap: 0.5,
          ...(expanded && { borderBottom: '1px solid', borderColor: 'divider' }),
          cursor: reorderable ? (isDragging ? 'grabbing' : 'grab') : undefined,
          touchAction: reorderable ? 'none' : undefined,
          '& .MuiAccordionSummary-content': { my: 0 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Typography variant="overline" color="text.secondary" fontWeight="medium">
            {section.name}
          </Typography>
          <Stack
            className="section-actions"
            direction="row"
            spacing={0.5}
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
            sx={{
              opacity: isMobile || isRenameOpen || isDeleteOpen ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
          >
            <RenameSectionButton section={section} name={section.name} onOpenChange={setIsRenameOpen} />
            <DeleteSectionButton section={section} onOpenChange={setIsDeleteOpen} />
            {reorderable && (
              <Tooltip title="Drag to reorder section">
                <IconButton
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    opacity: 0.6,
                    '&:hover': { opacity: 1, color: 'text.primary' },
                  }}
                >
                  <DragIndicatorOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 2,
            py: 2,
            px: 2,
            minHeight: 120,
            alignContent: 'center',
          }}
        >
          {renderBody()}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
